import { Check, Plus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface ViewTabsProps {
  /** `followed`: a list View the workspace follows, marked with a gold star before its name. */
  views: ReadonlyArray<{ id: string; name: string; followed?: boolean }>;
  activeViewId: string | null;
  isDirty: boolean;
  actions?: React.ReactNode;
  editingViewId?: string | null;
  editingValue?: string;
  onEditingValueChange?: (value: string) => void;
  onCommitEditingView?: () => void;
  onCancelEditingView?: () => void;
  onSelectBase: () => void;
  onSelectView: (viewId: string) => void;
  /** Optional: only reachable while `isDirty`, so a fixed-tab consumer can omit it. */
  onSaveAsNew?: () => void;
}

export function ViewTabs({
  views,
  activeViewId,
  isDirty,
  actions,
  editingViewId = null,
  editingValue = '',
  onEditingValueChange,
  onCommitEditingView,
  onCancelEditingView,
  onSelectBase,
  onSelectView,
  onSaveAsNew,
}: ViewTabsProps) {
  return (
    <div className="flex min-w-0 items-center border-b px-6" data-testid="view-tabs">
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          type="button"
          className={cn(
            'relative h-12 min-w-16 shrink-0 px-2 text-sm font-medium text-muted-foreground hover:text-foreground',
            activeViewId === null &&
              'text-foreground after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:bg-foreground',
          )}
          onClick={onSelectBase}
        >
          All
        </button>
        {views.map((view) => {
          const active = activeViewId === view.id;
          const editing = editingViewId === view.id;

          return (
            <div
              key={view.id}
              className={cn(
                'relative inline-flex h-12 min-w-16 shrink-0 items-center justify-center gap-2 px-2 text-sm font-medium text-muted-foreground',
                active &&
                  'text-foreground after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:bg-foreground',
              )}
            >
              {editing ? (
                <InlineViewNameInput
                  value={editingValue}
                  onChange={onEditingValueChange}
                  onCommit={onCommitEditingView}
                  onCancel={onCancelEditingView}
                />
              ) : (
                <button
                  type="button"
                  className="inline-flex h-full items-center gap-2 hover:text-foreground"
                  onClick={() => onSelectView(view.id)}
                >
                  {view.followed ? <FollowedMark /> : null}
                  {view.name}
                </button>
              )}
            </div>
          );
        })}
        {isDirty ? (
          <div className="ml-3 inline-flex h-12 shrink-0 items-center gap-3">
            <div className="h-5 w-px shrink-0 bg-border" aria-hidden />
            <button
              type="button"
              className="inline-flex h-12 shrink-0 items-center gap-1 text-sm font-medium text-foreground"
              onClick={() => onSaveAsNew?.()}
            >
              <Plus className="h-4 w-4" /> Save as new view
            </button>
          </div>
        ) : null}
      </div>
      {actions ? <div className="ml-4 shrink-0">{actions}</div> : null}
    </div>
  );
}

function InlineViewNameInput({
  value,
  onChange,
  onCommit,
  onCancel,
}: {
  value: string;
  onChange?: (value: string) => void;
  onCommit?: () => void;
  onCancel?: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const focusInput = () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    };

    focusInput();
    const timeout = window.setTimeout(focusInput, 0);
    const animationFrame = window.requestAnimationFrame(focusInput);

    return () => {
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      onBlur={() => onCommit?.()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onCommit?.();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          onCancel?.();
        }
      }}
      aria-label="View name"
      className="h-8 min-w-24 max-w-48 rounded-md border bg-background px-2 text-sm font-medium text-foreground shadow-sm outline-none ring-2 ring-ring"
    />
  );
}

/** A followed View's state: a check, as on the record's Following button; a star is only ever the action. */
function FollowedMark() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="-mr-1 inline-flex" aria-label="Following this view">
            <Check aria-hidden className="size-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent>Following this view</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
